import { useEffect } from "react";

/**
 * iter150AZ.69 — Permanent blocker shown when an email on the
 * hard-coded blocklist tries to authenticate. The component:
 *   1. Clears any stale local auth state (so a reload won't re-arm
 *      a half-set user object).
 *   2. Attempts to close the browser tab via window.close().
 *   3. Falls back to a full-screen, exit-less message if the browser
 *      refuses to close the tab (most do, unless the tab was opened
 *      via window.open).
 *
 * There is intentionally no "Try a different email" link, no logo,
 * no logout button. The screen is dead.
 */
export default function AccountLockedBlocker() {
  useEffect(() => {
    // Belt-and-braces: nuke any client state before attempting close.
    try { localStorage.removeItem("usair_user"); } catch { /* noop */ }
    try { localStorage.removeItem("usair_guest_email"); } catch { /* noop */ }
    try { sessionStorage.clear(); } catch { /* noop */ }
    // Best-effort close. Browsers refuse to close tabs they didn't
    // open programmatically, in which case the visible blocker below
    // remains on screen — the page is unrecoverable either way.
    try { window.close(); } catch { /* noop */ }
    // Also try replacing history so back-button can't escape.
    try {
      window.history.replaceState(null, "", window.location.pathname);
      window.history.pushState(null, "", window.location.pathname);
      const onPop = () => window.history.pushState(null, "", window.location.pathname);
      window.addEventListener("popstate", onPop);
      return () => window.removeEventListener("popstate", onPop);
    } catch { /* noop */ }
  }, []);

  return (
    <div
      data-testid="account-locked-blocker"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#000",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 24,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        userSelect: "none",
      }}
    >
      <div style={{ maxWidth: 460 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: 4,
            opacity: 0.55,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Access Permanently Denied
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.35 }}>
          This account is not allowed to use this application.
        </div>
        <div style={{ marginTop: 18, fontSize: 13, opacity: 0.55, lineHeight: 1.6 }}>
          You may close this tab.
        </div>
      </div>
    </div>
  );
}
